import { ref, computed } from 'vue';
import { t } from '@/utils/i18n-helper';
import cambioButacaService from '@/services/perfil/cambioButaca';
import { toError } from '@/services/perfil/_normalize';

/**
 * useCambioButacaButacas — Estado y operaciones del paso de selección de
 * asiento (butaca numerada o plaza) para el flujo de cambio de asiento.
 *
 * Responsabilidades:
 *  - Carga del mapa de asientos para la zona seleccionada.
 *  - Carga del SVG 3D de la zona (mapa interactivo de butacas).
 *  - Selección / deselección de un solo asiento (maxSeats=1). Acepta tanto
 *    butacas numeradas como plazas.
 *  - `asientosParaPicker` propaga `plaza_id`, `es_plaza`, `sector_name` además
 *    de `id`, `row`, `number`, `available`, `fila`, `asiento`, `precios` para
 *    que el orquestador pueda bifurcar por `tiene_plazas` per-zona o per-seat.
 *  - Filtro de `tiposAbonosDisponibles` por `abonadoDetail.descuento.renovacion`
 *    para que el selector de tipo de abono (antifluo) muestre sólo las
 *    opciones que aplican al abonado.
 *  - Guards: isAsientosEmpty, puedeAvanzarPasoButaca.
 *
 * A diferencia de invitaciones (selección múltiple), el cambio de asiento
 * opera con selección única: un abonado cambia UN asiento.
 *
 * @param {import('vue').Ref<Object|null>} [abonadoDetail] Ref al detalle del
 *        abonado que mantiene el orquestador. Si se inyecta, se usa para
 *        filtrar `tiposAbonosDisponibles` reactivamente.
 */
export function useCambioButacaButacas(abonadoDetail = null) {

    // ─── State ────────────────────────────────────────────────────────────

    /**
     * Respuesta del endpoint getAsientos (configuracion_asientos).
     */
    const asientosData = ref(null);

    /** String SVG del mapa 3D de la zona (para LeafletComponent). */
    const svgString = ref('');

    const loadingAsientos = ref(false);
    const errorAsientos = ref(null);

    /**
     * Butaca única seleccionada por el usuario.
     * Solo puede haber una seleccionada a la vez (maxSeats = 1).
     */
    const asientoSeleccionado = ref(null);

    /**
     * Tipos de abono crudos devueltos por el endpoint (sin filtrar).
     * Solo se popula en la rama antifluo (tipo_svg=1) desde
     * `GET /f/zonas/{id}?conf=true` (`response.data.data.tipos_abonos`).
     * En la rama Leaflet (tipo_svg=2) y en el camino `mapa-general` queda en `[]`.
     */
    const tiposAbonosRaw = ref([]);

    /**
     * ¿El descuento del abonado es de renovación?
     * `null` mientras `abonadoDetail` no ha cargado → no filtra hasta entonces
     * para no esconder opciones durante la carga inicial del detalle.
     */
    const abonadoEsRenovacion = computed(() => {
        const renov = abonadoDetail?.value?.descuento?.renovacion;
        return renov != null ? Boolean(renov) : null;
    });

    /**
     * Tipos de abono disponibles para el selector, filtrados por
     * `abonadoEsRenovacion`. Si el descuento no está cargado todavía
     * (`null`), devuelve el array crudo.
     */
    const tiposAbonosDisponibles = computed(() => {
        if (!tiposAbonosRaw.value.length) return [];
        if (abonadoEsRenovacion.value === null) return tiposAbonosRaw.value;
        return tiposAbonosRaw.value.filter(
            t => Boolean(t.renovacion) === abonadoEsRenovacion.value
        );
    });

    // ─── Computed ─────────────────────────────────────────────────────────

    /**
     * `true` si los asientos se cargaron sin error pero no hay datos útiles.
     */
    const isAsientosEmpty = computed(
        () => !loadingAsientos.value && !errorAsientos.value && !asientosData.value
    );

    /**
     * Paso Butaca → puede avanzar si hay exactamente 1 butaca seleccionada.
     */
    const puedeAvanzarPasoButaca = computed(
        () => Boolean(asientoSeleccionado.value)
    );

    /**
     * Normaliza los asientos de `configuracion_asientos` al formato universal
     * que los pickers de butacas consumen. El dominio universal incluye
     * butacas numeradas (Camino 1) y plazas (Camino 2).
     *
     * `configuracion_asientos` es un objeto cuyas claves son nombres de fila y cuyos
     * valores son arrays de asientos con forma:
     *   `{ id, fila, asiento, ocupado, precios_count, precios, plaza_id, es_plaza, sector_name }`
     *
     * Salida (asiento universal):
     *   Butaca numerada: `{ id, row, number, available, fila, asiento, plaza_id: null, es_plaza: false, sector_name, precios }`
     *   Plaza:           `{ id, row, number, available, fila, asiento, plaza_id, es_plaza: true, sector_name, precios }`
     *
     * El orquestador bifurca por `zona.tiene_plazas` (per-zona) o por `seat.es_plaza`
     * (per-seat) para decidir Camino 1 o Camino 2 en la reserva.
     */
    const asientosParaPicker = computed(() => {
        const conf = asientosData.value?.configuracion_asientos;
        if (!conf) return [];
        const seats = [];
        for (const entries of Object.values(conf)) {
            for (const s of entries) {
                const esPlaza = Boolean(s.es_plaza);
                seats.push({
                    id: s.id,
                    row: String(s.fila ?? ''),
                    number: Number(s.asiento ?? s.numero ?? 0),
                    available: s.ocupado === 0 && s.precios_count > 0,
                    // Preservar datos originales para el flujo de reserva
                    fila: s.fila,
                    asiento: s.asiento ?? s.numero ?? null,
                    // Plaza fields: en butacas van a null; en plazas propagan id+sector
                    plaza_id: esPlaza ? (s.plaza_id ?? s.id ?? null) : null,
                    es_plaza: esPlaza,
                    sector_name: s.sector_name ?? s.sector ?? null,
                    precios: s.precios || [],
                });
            }
        }
        return seats;
    });

    // ─── API: carga de asientos + SVG ─────────────────────────────────────

    /**
     * Carga las butacas disponibles para la zona seleccionada.
     *
     * El endpoint usado depende del valor de `tipoSvg` (manejado internamente en el service).
     * Se carga además el SVG 3D de la zona para LeafletComponent cuando el flujo lo
     * requiere: (tipo_svg=2) o (tipo_svg=1 con tipo_proceso_online === 2, camino secciones).
     * Para el resto de casos tipo_svg=1 no se carga SVG (usa SeatSelectionCard con datos grid).
     *
     * @param {string|number} abonadoId
     * @param {string|number} zonaId
     * @param {string|number} recintoId
     * @param {string|number} listaAbonadoId
     * @param {string}        [hashTeatro]  Hash del teatro (opcional, se lee de localStorage si no se pasa)
     * @param {number|null}   [tipoSvg]     Tipo de SVG del recinto (1=asientos normales, 2=Leaflet 3D)
     * @param {number|null}   [tipoProcesoOnline] Tipo de proceso online (1=regiones, 2=secciones, 3=mapa general)
     * @param {boolean}       [renovacion] Si true, el backend devuelve sólo precios de renovación
     * @param {boolean}       [tienePlazas] Si la zona tiene plazas no numeradas. Para `tipo_svg=1`
     *        + `tipo_proceso_online=1` bifurca: con plazas carga butacas DettallesButacas + SVG de
     *        la región (Leaflet); sin plazas usa el grid antifluo.
     */
    const cargarAsientos = async(abonadoId, zonaId, recintoId, listaAbonadoId, hashTeatro, tipoSvg, tipoProcesoOnline, renovacion, tienePlazas) => {
        if (!abonadoId || !zonaId || !recintoId || !listaAbonadoId) {
            tiposAbonosRaw.value = [];
            return;
        }

        loadingAsientos.value = true;
        errorAsientos.value = null;
        svgString.value = '';
        asientoSeleccionado.value = null;
        try {
            const h = hashTeatro || localStorage.getItem('hash_teatro');

            const result = await cambioButacaService.getAsientos(abonadoId, recintoId, zonaId, listaAbonadoId, h, tipoSvg, tipoProcesoOnline, renovacion, tienePlazas);

            asientosData.value = result;
            tiposAbonosRaw.value = result.tipos_abonos ?? [];

            // Cargar SVG solo en la rama Leaflet:
            //  - (tipo_svg === 2): Leaflet 3D clásico.
            //  - (tipo_svg === 1 && tipo_proceso_online === 2): camino secciones con plazas.
            //  - (tipo_svg === 1 && tipo_proceso_online === 1 && tienePlazas): regiones con
            //    plazas — la región se renderiza con Leaflet sobre el SVG de la zona
            //    (`getSvgZona`) + butacas DettallesButacas (patrón legacy
            //    `SeleccionarAsientosRegionAnexa`). Si `getSvgZona` falla/404, `svgString`
            //    queda `''` y el paso cae al grid con los datos de butacas (caso borde).
            // Misma condición que `CambioAsientoPasoRegiones.vue` decide Leaflet 3D.
            // Para antifluo (tipo_svg !== 2 && proceso !== 2) sin plazas no se
            // usa: el grid usa `configuracion_asientos` directamente.
            if (tipoSvg === 2
                || (tipoSvg === 1 && tipoProcesoOnline === 2)
                || (tipoSvg === 1 && tipoProcesoOnline === 1 && tienePlazas)) {
                try {
                    const svg = await cambioButacaService.getSvgZona(recintoId, zonaId, listaAbonadoId, h);
                    svgString.value = svg || '';
                } catch (_) {
                    svgString.value = '';
                }
            }
        } catch (err) {
            const normalized = toError(err);
            errorAsientos.value = normalized.message ??
                t('perfil.cambio_asiento.stepper.error_asientos');
            asientosData.value = null;
            svgString.value = '';
            tiposAbonosRaw.value = [];
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaButacas] Error al obtener asientos:', err);
            }
        } finally {
            loadingAsientos.value = false;
        }
    };

    /**
     * Carga el mapa general del recinto (camino 'mapa-general',
     * `tipo_proceso_online=3`). No requiere zona.
     *
     * Recupera el SVG del recinto completo (`GET /f/recinto/{recinto_id}`,
     * mismo endpoint que la venta de abonos online en `SeleccionarAsientosMapaGeneral`)
     * y las butacas de todo el recinto. El SVG se renderiza con Leaflet y las
     * butacas se pintan sobre él por `svg_id` (igual que `SeleccionarAsientosMapaGeneral`).
     *
     * Side effects:
     *  - `asientosData.value` ← datos de butacas del recinto
     *  - `svgString.value` ← SVG del recinto completo (para LeafletComponent)
     *  - `loadingAsientos` / `errorAsientos` ← estado reactivo
     *
     * @param {string|number} abonadoId
     * @param {string|number} recintoId
     * @param {string|number} listaAbonadoId
     * @param {string}        [hashTeatro]  Hash del teatro (opcional, se lee de localStorage si no se pasa)
     * @param {boolean}       [renovacion=false] Si true, el backend devuelve sólo precios de renovación
     * @returns {Promise<void>}
     */
    const cargarMapaGeneral = async(abonadoId, recintoId, listaAbonadoId, hashTeatro, renovacion = false) => {
        if (!abonadoId || !recintoId || !listaAbonadoId) return;

        loadingAsientos.value = true;
        errorAsientos.value = null;
        svgString.value = '';
        asientoSeleccionado.value = null;
        try {
            const h = hashTeatro || localStorage.getItem('hash_teatro');

            // Mapa general: SVG del recinto completo + butacas de todo el
            // recinto. El SVG se pinta con Leaflet y cada butaca se mapea por
            // `svg_id` (mismo patrón que `SeleccionarAsientosMapaGeneral`).
            const [svg, butacas] = await Promise.all([
                cambioButacaService.getRecintoSvg(recintoId),
                cambioButacaService.getButacasByRecinto(abonadoId, recintoId, listaAbonadoId, h, renovacion),
            ]);

            asientosData.value = butacas;
            svgString.value = svg || '';
        } catch (err) {
            const normalized = toError(err);
            errorAsientos.value = normalized.message ??
                t('perfil.cambio_asiento.stepper.error_asientos');
            asientosData.value = null;
            svgString.value = '';
            if (process.env.NODE_ENV !== 'production') {
                console.error('[useCambioButacaButacas] Error al cargar mapa general:', err);
            }
        } finally {
            loadingAsientos.value = false;
        }
    };

    // ─── Selección de asiento ─────────────────────────────────────────────

    /**
     * Selecciona o deselecciona la butaca única.
     * Dado que maxSeats=1, al seleccionar una butaca nueva se reemplaza la anterior.
     *
     * Normaliza los campos del asiento para que siempre tenga `row` y `number`
     * independientemente de si viene del SVG (fila/asiento) o del SeatPicker (row/number).
     *
     * @param {Object} raw  Objeto asiento crudo (puede tener fila/asiento o row/number)
     * @returns {{ added: boolean, removed: boolean }}
     */
    const toggleAsiento = raw => {
        const asiento = {
            ...raw,
            row:    raw.row    ?? raw.fila    ?? '',
            number: raw.number ?? raw.asiento ?? raw.numero ?? '',
        };

        if (asientoSeleccionado.value && asientoSeleccionado.value.id === asiento.id) {
            asientoSeleccionado.value = null;
            return { added: false, removed: true };
        }
        asientoSeleccionado.value = asiento;
        return { added: true, removed: false };
    };

    /**
     * Quita el asiento seleccionado.
     */
    const quitarAsiento = () => {
        asientoSeleccionado.value = null;
    };

    // ─── Reset ────────────────────────────────────────────────────────────
    const reset = () => {
        asientosData.value = null;
        svgString.value = '';
        asientoSeleccionado.value = null;
        errorAsientos.value = null;
        tiposAbonosRaw.value = [];
    };

    // ─── API ──────────────────────────────────────────────────────────────
    return {
        asientosData,
        svgString,
        asientosParaPicker,
        loadingAsientos,
        errorAsientos,
        asientoSeleccionado,
        tiposAbonosDisponibles,
        cargarAsientos,
        cargarMapaGeneral,
        toggleAsiento,
        quitarAsiento,
        isAsientosEmpty,
        puedeAvanzarPasoButaca,
        reset,
    };
}
